import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { useOfflineMutation } from '../../../hooks/useOfflineMutation';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function NouveauPatientPage() {
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [medecinInfo, setMedecinInfo] = useState<any>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [potentialMatches, setPotentialMatches] = useState<any[]>([]);
  const [showMatchDialog, setShowMatchDialog] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<any>(null);
  const [generatedActivationCode, setGeneratedActivationCode] = useState<string | null>(null);
  const { mutate } = useOfflineMutation<any>();

  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    telephone: '',
    email: '',
    date_naissance: '',
    date_rdv: '',
    heure_debut: '',
    heure_fin: '',
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    if (role !== 'medecin' && role !== 'super_admin') { navigate('/', { replace: true }); return; }
    loadMedecinInfo();
  }, [user, role, authLoading]);

  const loadMedecinInfo = async () => {
    if (!user) return;
    const { data, error } = await supabase.from('medecins').select('*').eq('user_id', user.id).single();
    if (error) { console.error('Erreur chargement médecin:', error); return; }
    setMedecinInfo(data);
  };

  const checkForMatches = async () => {
    if (!formData.nom || !formData.prenom || !formData.date_naissance) { setPotentialMatches([]); return; }
    try {
      const { data, error } = await supabase.from('patients').select('*').ilike('nom', formData.nom).ilike('prenom', formData.prenom).eq('date_naissance', formData.date_naissance);
      if (error) throw error;
      if (data && data.length > 0) { setPotentialMatches(data); setShowMatchDialog(true); } else { setPotentialMatches([]); }
    } catch (err) { console.error('Erreur recherche correspondances:', err); }
  };

  useEffect(() => {
    const timer = setTimeout(() => { checkForMatches(); }, 500);
    return () => clearTimeout(timer);
  }, [formData.nom, formData.prenom, formData.date_naissance]);

  const confirmMatch = (patient: any) => {
    setSelectedMatch(patient);
    setFormData({ ...formData, telephone: patient.telephone || '', email: patient.email || '' });
    setShowMatchDialog(false);
    setPotentialMatches([]);
  };

  const rejectMatches = () => { setShowMatchDialog(false); setPotentialMatches([]); setSelectedMatch(null); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.nom || !formData.prenom || !formData.date_naissance) {
      setError('Le nom, prénom et date de naissance sont obligatoires');
      return;
    }
    if (!formData.date_rdv || !formData.heure_debut || !formData.heure_fin) {
      setError('Les informations du rendez-vous sont obligatoires');
      return;
    }

    setLoading(true);
    const online = navigator.onLine;

    try {
      let patientId: string | undefined;
      let isNewProvisionalPatient = false;
      let activationCodeValue: string | null = null;

      if (selectedMatch) {
        patientId = selectedMatch.id;
      } else {
        // Vérifier patients existants (seulement en ligne)
        if (online) {
          const { data: existingPatients } = await supabase
            .from('patients').select('*')
            .ilike('nom', formData.nom).ilike('prenom', formData.prenom)
            .eq('date_naissance', formData.date_naissance);

          if (existingPatients && existingPatients.length > 0) {
            setError('Un patient correspondant existe. Veuillez confirmer son identité avant de continuer.');
            setPotentialMatches(existingPatients);
            setShowMatchDialog(true);
            setLoading(false);
            return;
          }
        }

        if (!formData.telephone && !formData.email) {
          setError('Au moins un moyen de contact (téléphone ou email) est requis');
          setLoading(false);
          return;
        }

        // Obtenir le numéro de dossier (seulement en ligne)
        let numeroDossier: string | null = null;
        if (online) {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            const resp = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/obtenir-prochain-numero-dossier`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${session?.access_token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({}),
            });
            const result = await resp.json();
            if (result.success && result.numero_dossier) numeroDossier = result.numero_dossier;
          } catch (numErr) { console.error('Erreur attribution numéro dossier:', numErr); }
        }

        // Créer le patient via le hook offline-first
        const patientData = {
          nom: formData.nom, prenom: formData.prenom,
          telephone: formData.telephone || null, email: formData.email || null,
          date_naissance: formData.date_naissance,
          numero_dossier: numeroDossier,
          is_provisional: true, status: 'PENDING_ACCOUNT', user_id: null,
        };

        const patientResult = await mutate({
          table: 'patients', operation: 'INSERT', data: patientData,
          metadata: { summary: `Patient ${formData.prenom} ${formData.nom}`, performedBy: role || 'medecin' },
          onlineExecutor: async () => {
            const { data: pd, error: pe } = await supabase.from('patients').insert(patientData).select().single();
            if (pe) throw pe;
            return pd;
          },
        });

        patientId = patientResult.id;
        isNewProvisionalPatient = true;

        // Invitation (seulement en ligne)
        if (online && (formData.email || formData.telephone)) {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-patient-invitation`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${session?.access_token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({
                patientName: `${formData.prenom} ${formData.nom}`,
                patientEmail: formData.email || null,
                patientPhone: formData.telephone || null,
                appointmentDate: formData.date_rdv,
                appointmentTime: formData.heure_debut,
                doctorName: `Dr. ${medecinInfo.nom} ${medecinInfo.prenom}`,
              }),
            });
          } catch (inviteError) { console.error('Erreur envoi invitation:', inviteError); }
        }

        // Générer un code d'activation (seulement en ligne)
        if (online && numeroDossier) {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            const codeRes = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/generate-patient-activation-code`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${session?.access_token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ patientId, numeroDossier }),
            });
            const codeData = await codeRes.json();
            if (codeData.success && codeData.code) {
              activationCodeValue = codeData.code;
              setGeneratedActivationCode(codeData.code);
            }
          } catch (codeErr) { console.error('Erreur génération code activation:', codeErr); }
        }
      }

      // Créer le rendez-vous via le hook offline-first
      await mutate({
        table: 'rendez_vous', operation: 'INSERT',
        data: {
          patient_id: patientId, medecin_id: medecinInfo.id,
          date_rdv: formData.date_rdv, heure_debut: formData.heure_debut, heure_fin: formData.heure_fin,
          statut: 'en_attente',
        },
        metadata: { summary: `RDV ${formData.prenom} ${formData.nom} - ${formData.date_rdv} ${formData.heure_debut}`, performedBy: role || 'medecin' },
        onlineExecutor: async () => {
          const { data: rd, error: re } = await supabase.from('rendez_vous').insert({
            patient_id: patientId, medecin_id: medecinInfo.id,
            date_rdv: formData.date_rdv, heure_debut: formData.heure_debut, heure_fin: formData.heure_fin,
            statut: 'en_attente',
          }).select().single();
          if (re) throw re;
          return rd;
        },
      });

      // Notification médecin (seulement en ligne)
      if (online && isNewProvisionalPatient) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-notification`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${session?.access_token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: medecinInfo.user_id, type: 'nouveau_rdv',
              titre: 'Nouveau rendez-vous créé',
              message: `Rendez-vous créé pour ${formData.prenom} ${formData.nom} (patient provisoire) le ${new Date(formData.date_rdv).toLocaleDateString('fr-FR')} à ${formData.heure_debut}`,
              lien: '/medecin/rendez-vous',
            }),
          });
        } catch (notifError) { console.error('Erreur envoi notification:', notifError); }
      }

      const msg = online
        ? `Rendez-vous créé avec succès pour ${formData.prenom} ${formData.nom} le ${new Date(formData.date_rdv).toLocaleDateString('fr-FR')} à ${formData.heure_debut}.`
        : 'Rendez-vous enregistré en mode hors-ligne. Synchronisation automatique au retour de la connexion.';

      setSuccess(msg);
      setFormData({ nom: '', prenom: '', telephone: '', email: '', date_naissance: '', date_rdv: '', heure_debut: '', heure_fin: '' });
      setSelectedMatch(null);
      if (!activationCodeValue) {
        setTimeout(() => navigate('/medecin/rendez-vous'), 2000);
      }
    } catch (err: any) {
      console.error('Erreur:', err);
      setError(err.message || 'Une erreur est survenue lors de la création du rendez-vous');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin mb-4"></i>
          <div className="text-clinic-blue-dark text-xl">Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Nouveau Rendez-vous</h1>
              <p className="mt-2 text-gray-600">Créer un rendez-vous pour un patient</p>
            </div>
            <button onClick={() => navigate('/medecin/rendez-vous')} className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-arrow-left-line"></i> Retour
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white p-8 rounded-3xl shadow-xl border-2 border-clinic-blue/10">
            {error && (
              <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 text-red-600 rounded-2xl flex items-start">
                <i className="ri-error-warning-line text-xl mr-2 mt-0.5"></i><span>{error}</span>
              </div>
            )}
            {success && (
              <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 text-green-600 rounded-2xl flex items-start">
                <i className="ri-check-line text-xl mr-2 mt-0.5"></i><span>{success}</span>
              </div>
            )}
            {selectedMatch && (
              <div className="mb-8 p-4 bg-green-50 border-2 border-green-200 rounded-2xl">
                <div className="flex items-start justify-between">
                  <div className="flex items-start">
                    <i className="ri-user-check-line text-green-600 text-xl mr-2 mt-0.5"></i>
                    <div>
                      <p className="font-semibold text-green-800">Patient confirmé</p>
                      <p className="text-green-700 mt-1">{selectedMatch.prenom} {selectedMatch.nom}</p>
                      <div className="flex gap-4 mt-1 text-sm text-green-600">
                        {selectedMatch.telephone && (<span className="flex items-center gap-1"><i className="ri-phone-line"></i>{selectedMatch.telephone}</span>)}
                        {selectedMatch.email && (<span className="flex items-center gap-1"><i className="ri-mail-line"></i>{selectedMatch.email}</span>)}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => setSelectedMatch(null)} className="text-green-600 hover:text-green-800 cursor-pointer"><i className="ri-close-line text-xl"></i></button>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
              <div>
                <h2 className="text-xl font-bold text-clinic-blue-dark mb-4 flex items-center"><i className="ri-user-line mr-2"></i>Informations du patient</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-clinic-blue-dark mb-2">Nom *</label>
                    <input type="text" required value={formData.nom} onChange={(e) => setFormData({ ...formData, nom: e.target.value })} disabled={!!selectedMatch} className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm disabled:bg-gray-100" placeholder="Nom du patient" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-clinic-blue-dark mb-2">Prénom *</label>
                    <input type="text" required value={formData.prenom} onChange={(e) => setFormData({ ...formData, prenom: e.target.value })} disabled={!!selectedMatch} className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm disabled:bg-gray-100" placeholder="Prénom du patient" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-clinic-blue-dark mb-2">Date de naissance *</label>
                    <input type="date" required value={formData.date_naissance} onChange={(e) => setFormData({ ...formData, date_naissance: e.target.value })} disabled={!!selectedMatch} className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm disabled:bg-gray-100" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-clinic-blue-dark mb-2">Téléphone {!selectedMatch && '*'}</label>
                    <input type="tel" value={formData.telephone} onChange={(e) => setFormData({ ...formData, telephone: e.target.value })} disabled={!!selectedMatch} className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm disabled:bg-gray-100" placeholder="+33 6 12 34 56 78" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-clinic-blue-dark mb-2">Email {!selectedMatch && '*'}</label>
                    <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} disabled={!!selectedMatch} className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm disabled:bg-gray-100" placeholder="email@exemple.com" />
                  </div>
                </div>
                {!selectedMatch && (
                  <div className="mt-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-2xl">
                    <div className="flex items-start"><i className="ri-information-line text-blue-600 text-xl mr-2 mt-0.5"></i>
                      <div className="text-sm text-blue-800"><p className="font-semibold mb-1">Au moins un contact requis</p><p>Veuillez fournir au minimum un numéro de téléphone OU un email pour créer un patient provisoire.</p></div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <h2 className="text-xl font-bold text-clinic-blue-dark mb-4 flex items-center"><i className="ri-calendar-line mr-2"></i>Rendez-vous</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div><label className="block text-sm font-medium text-clinic-blue-dark mb-2">Date *</label><input type="date" required value={formData.date_rdv} onChange={(e) => setFormData({ ...formData, date_rdv: e.target.value })} className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm" /></div>
                  <div><label className="block text-sm font-medium text-clinic-blue-dark mb-2">Heure de début *</label><input type="time" required value={formData.heure_debut} onChange={(e) => setFormData({ ...formData, heure_debut: e.target.value })} className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm" /></div>
                  <div><label className="block text-sm font-medium text-clinic-blue-dark mb-2">Heure de fin *</label><input type="time" required value={formData.heure_fin} onChange={(e) => setFormData({ ...formData, heure_fin: e.target.value })} className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm" /></div>
                </div>
              </div>

              <div className="flex gap-4 justify-end pt-4">
                <button type="button" onClick={() => navigate('/medecin/rendez-vous')} className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-all font-semibold whitespace-nowrap cursor-pointer">Annuler</button>
                <button type="submit" disabled={loading} className="px-6 py-3 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-blue transition-all font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer">
                  {loading ? (<><i className="ri-loader-4-line animate-spin mr-2"></i>Création en cours...</>) : (<><i className="ri-calendar-check-line mr-2"></i>Créer le rendez-vous</>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {showMatchDialog && potentialMatches.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-2xl w-full shadow-2xl">
            <div className="flex items-start mb-6">
              <div className="w-12 h-12 bg-yellow-100 rounded-2xl flex items-center justify-center mr-4"><i className="ri-alert-line text-2xl text-yellow-600"></i></div>
              <div className="flex-1"><h3 className="text-2xl font-bold text-gray-900 mb-2">Patient potentiellement trouvé</h3><p className="text-gray-600">Un ou plusieurs patients avec le même nom, prénom et date de naissance existent déjà dans le système.</p></div>
            </div>
            <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-2xl">
              <div className="flex items-start"><i className="ri-question-line text-blue-600 text-xl mr-2 mt-0.5"></i><div className="text-sm text-blue-800"><p className="font-semibold mb-1">Veuillez demander au patient :</p><p>"Est-ce que c'est votre compte avec les informations suivantes ?"</p></div></div>
            </div>
            <div className="space-y-3 mb-6 max-h-64 overflow-y-auto">
              {potentialMatches.map((patient) => (
                <div key={patient.id} className="p-4 bg-gray-50 border-2 border-gray-200 rounded-2xl hover:border-clinic-blue transition-all">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{patient.prenom} {patient.nom}</p>
                      <p className="text-sm text-gray-600 mt-1">Né(e) le {new Date(patient.date_naissance).toLocaleDateString('fr-FR')}</p>
                      <div className="flex gap-4 mt-2 text-sm">
                        {patient.telephone && (<span className="flex items-center gap-1 text-gray-700"><i className="ri-phone-line text-clinic-blue"></i>{patient.telephone}</span>)}
                        {patient.email && (<span className="flex items-center gap-1 text-gray-700"><i className="ri-mail-line text-clinic-blue"></i>{patient.email}</span>)}
                      </div>
                    </div>
                    <button onClick={() => confirmMatch(patient)} className="ml-4 px-4 py-2 bg-clinic-green text-white rounded-full hover:bg-clinic-green/90 transition-all font-semibold whitespace-nowrap cursor-pointer"><i className="ri-check-line mr-1"></i>C'est lui</button>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-4 justify-end pt-4 border-t-2 border-gray-200">
              <button onClick={rejectMatches} className="px-6 py-3 bg-gray-600 text-white rounded-full hover:bg-gray-700 transition-all font-semibold whitespace-nowrap cursor-pointer"><i className="ri-close-line mr-2"></i>Ce n'est pas lui - Créer un patient provisoire</button>
            </div>
          </div>
        </div>
      )}
      {generatedActivationCode && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 sm:p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-clinic-green/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-key-2-line text-3xl text-clinic-green"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Code d'activation généré</h3>
              <p className="text-sm text-gray-600">
                Remettez ce code au patient. Il lui servira à créer son compte et accéder à son dossier médical.
              </p>
            </div>
            <div className="bg-gray-50 border-2 border-dashed border-clinic-green/40 rounded-2xl p-5 text-center mb-4">
              <p className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wide">Code d'activation (valable 7 jours)</p>
              <p className="text-2xl font-mono font-bold text-clinic-blue-dark tracking-widest">{generatedActivationCode}</p>
            </div>
            <p className="text-xs text-gray-500 mb-6 text-center">
              <i className="ri-information-line mr-1"></i>
              Le patient se rendra sur « Créer mon compte patient » et saisira ce code + son numéro de dossier.
            </p>
            <button
              onClick={() => { setGeneratedActivationCode(null); navigate('/medecin/rendez-vous'); }}
              className="w-full px-6 py-3 bg-clinic-green text-white rounded-full hover:bg-clinic-green/90 transition-all font-semibold whitespace-nowrap cursor-pointer"
            >
              J'ai remis le code au patient
            </button>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}